import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, BarChart3, Package, Users, Monitor, Smartphone,
  ArrowRight, CheckCircle, Zap, Shield, Globe, TrendingUp,
  LogIn, User, Lock, X, Eye, EyeOff, Menu
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { secureStorage } from '../utils/security';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// Vendix brand colors
const BRAND = {
  blue: '#0078D4',
  lightBlue: '#00ADEF',
  green: '#33CC66',
  dark: '#222933',
  lightBg: '#f4f8fc'
};

const ICON_MAP = {
  'Punto de Venta': ShoppingCart,
  'Inventario': Package,
  'Tienda Online': Globe,
  'Estadísticas': BarChart3,
  'Multi-usuario': Users,
  'Seguridad': Shield
};

const LandingPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [config, setConfig] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/landing-config`)
      .then(r => r.json())
      .then(setConfig)
      .catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error('Completa todos los campos');
      return;
    }
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        username: loginForm.username,
        password: loginForm.password
      });
      const { access_token, username, store_id, store_name, role, admin_path, store_domain } = res.data;
      secureStorage.set('admin_token', access_token);
      secureStorage.set('admin_username', username);
      secureStorage.set('admin_store_id', store_id);
      secureStorage.set('admin_store_name', store_name || '');
      secureStorage.set('admin_store_domain', store_domain || '');
      secureStorage.set('admin_role', role || 'admin');
      secureStorage.set('admin_path', admin_path || '');
      toast.success(`Bienvenido, ${username}`);
      const path = admin_path || `admin-${store_domain}`;
      setTimeout(() => { window.location.href = `/${path}/dashboard`; }, 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Usuario o contraseña incorrectos');
    } finally {
      setLoginLoading(false);
    }
  };

  const c = config || {
    brand_name: 'Vendix',
    hero_badge: 'Sistema POS #1 para negocios en Ecuador',
    hero_title_prefix: 'Tu negocio,',
    hero_title_highlight: 'bajo control',
    hero_title_suffix: 'total',
    hero_subtitle: 'Sistema completo de punto de venta, inventario, tienda online y estadísticas.',
    cta_text: 'Solicitar Demo',
    whatsapp_number: '593999999999',
    features: [],
    plans: [],
    contact_title: 'Empieza hoy',
    contact_subtitle: 'Contáctanos por WhatsApp',
    footer_tagline: 'Sistema de gestión para negocios en Ecuador'
  };

  const waLink = `https://wa.me/${c.whatsapp_number}?text=Hola%2C%20quiero%20información%20sobre%20${encodeURIComponent(c.brand_name)}`;

  return (
    <div style={{ background: '#fff', overflowX: 'hidden' }}>
      {/* Navigation */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(16px)', borderBottom: '1px solid #e8edf2' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '12px 16px' : '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <img src={`${BACKEND_URL}/static/images/vendix-logo.jpeg`} alt="Vendix" style={{ height: isMobile ? '30px' : '38px', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
            <span style={{ fontWeight: 800, fontSize: isMobile ? '1.2rem' : '1.5rem', color: BRAND.dark, letterSpacing: '-0.5px' }} data-testid="brand-name">
              {c.brand_name}<sup style={{ fontSize: '0.5em', color: BRAND.blue }}>TM</sup>
            </span>
          </div>
          {isMobile ? (
            <button onClick={() => setMobileMenu(!mobileMenu)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }} data-testid="mobile-menu-toggle">
              <Menu size={24} color={BRAND.dark} />
            </button>
          ) : (
            <nav style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <a href="#funciones" style={{ color: BRAND.dark, textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500, opacity: 0.7 }}>Funciones</a>
              <a href="#planes" style={{ color: BRAND.dark, textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500, opacity: 0.7 }}>Planes</a>
              <button onClick={() => setShowLogin(true)}
                style={{ padding: '8px 18px', borderRadius: '8px', background: 'transparent', border: `1.5px solid ${BRAND.blue}`, color: BRAND.blue, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                data-testid="nav-login">
                Iniciar Sesión
              </button>
              <a href="/registro"
                style={{ padding: '8px 20px', borderRadius: '8px', background: BRAND.blue, color: 'white', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}
                data-testid="nav-register">
                Regístrate Gratis
              </a>
            </nav>
          )}
        </div>
        {/* Mobile Menu Dropdown */}
        {isMobile && mobileMenu && (
          <div style={{ background: 'white', borderTop: '1px solid #e8edf2', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <a href="#funciones" onClick={() => setMobileMenu(false)} style={{ color: BRAND.dark, textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500, padding: '8px 0' }}>Funciones</a>
            <a href="#planes" onClick={() => setMobileMenu(false)} style={{ color: BRAND.dark, textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500, padding: '8px 0' }}>Planes</a>
            <button onClick={() => { setShowLogin(true); setMobileMenu(false); }}
              style={{ padding: '10px', borderRadius: '8px', background: 'transparent', border: `1.5px solid ${BRAND.blue}`, color: BRAND.blue, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', textAlign: 'center' }}
              data-testid="mobile-login">
              Iniciar Sesión
            </button>
            <a href="/registro"
              style={{ padding: '10px', borderRadius: '8px', background: BRAND.blue, color: 'white', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', textAlign: 'center' }}
              data-testid="mobile-register">
              Regístrate Gratis
            </a>
          </div>
        )}
      </header>

      {/* Hero */}
      <section style={{ minHeight: isMobile ? 'auto' : '88vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: isMobile ? '40px 16px 30px' : '80px 24px 60px', background: `linear-gradient(180deg, ${BRAND.lightBg} 0%, #ffffff 100%)` }}>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '50px', background: '#e8f4fd', border: `1px solid ${BRAND.lightBlue}40`, marginBottom: isMobile ? '16px' : '28px' }}>
            <TrendingUp size={14} color={BRAND.blue} />
            <span style={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 600, color: BRAND.blue }}>{c.hero_badge}</span>
          </div>
          <h1 style={{ fontSize: isMobile ? '1.8rem' : 'clamp(2.5rem, 6vw, 4.2rem)', fontWeight: 900, color: BRAND.dark, lineHeight: 1.08, marginBottom: '20px', maxWidth: '800px', margin: '0 auto 16px', padding: isMobile ? '0 8px' : 0 }} data-testid="hero-title">
            {c.hero_title_prefix}{' '}
            <span style={{ background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.green})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {c.hero_title_highlight}
            </span>{' '}
            {c.hero_title_suffix}
          </h1>
          <p style={{ fontSize: isMobile ? '0.9rem' : '1.15rem', color: '#5a6577', maxWidth: '560px', margin: '0 auto 28px', lineHeight: 1.7, padding: isMobile ? '0 8px' : 0 }}>
            {c.hero_subtitle}
          </p>
          <div style={{ display: 'flex', gap: isMobile ? '10px' : '14px', justifyContent: 'center', flexWrap: 'wrap', padding: isMobile ? '0 8px' : 0 }}>
            <a href="/registro"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: isMobile ? '12px 24px' : '16px 36px', borderRadius: '12px', background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.lightBlue})`, color: 'white', fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1.05rem', textDecoration: 'none', boxShadow: `0 8px 30px ${BRAND.blue}30`, flex: isMobile ? '1 1 auto' : 'none', justifyContent: 'center' }}
              data-testid="cta-register">
              Regístrate Gratis
              <ArrowRight size={18} />
            </a>
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: isMobile ? '12px 24px' : '16px 36px', borderRadius: '12px', background: 'white', color: BRAND.dark, fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1.05rem', textDecoration: 'none', border: '1.5px solid #e0e5ec', flex: isMobile ? '1 1 auto' : 'none', justifyContent: 'center' }}
              data-testid="cta-whatsapp">
              {c.cta_text}
            </a>
          </div>
          {!isMobile && (
            <motion.div style={{ marginTop: '60px', display: 'flex', justifyContent: 'center', gap: '20px', alignItems: 'flex-end' }}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
              <div style={{ width: '160px', padding: '20px', background: 'white', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.06)', border: '1px solid #e8edf2', textAlign: 'center' }}>
                <Smartphone size={30} color={BRAND.blue} style={{ margin: '0 auto 8px' }} />
                <p style={{ fontSize: '0.75rem', color: '#7a8599', fontWeight: 600 }}>Desde el celular</p>
              </div>
              <div style={{ width: '260px', padding: '24px', background: 'white', borderRadius: '20px', boxShadow: `0 20px 60px ${BRAND.blue}12`, border: `1px solid ${BRAND.blue}20`, textAlign: 'center' }}>
                <Monitor size={36} color={BRAND.blue} style={{ margin: '0 auto 8px' }} />
                <p style={{ fontSize: '0.85rem', color: '#7a8599', fontWeight: 600 }}>Cualquier computador</p>
              </div>
              <div style={{ width: '160px', padding: '20px', background: 'white', borderRadius: '16px', boxShadow: '0 12px 40px rgba(0,0,0,0.06)', border: '1px solid #e8edf2', textAlign: 'center' }}>
                <Globe size={30} color={BRAND.green} style={{ margin: '0 auto 8px' }} />
                <p style={{ fontSize: '0.75rem', color: '#7a8599', fontWeight: 600 }}>Tienda online</p>
              </div>
            </motion.div>
          )}
          {isMobile && (
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <div style={{ flex: 1, padding: '14px 8px', background: 'white', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', border: '1px solid #e8edf2', textAlign: 'center' }}>
                <Smartphone size={22} color={BRAND.blue} style={{ margin: '0 auto 6px' }} />
                <p style={{ fontSize: '0.65rem', color: '#7a8599', fontWeight: 600 }}>Celular</p>
              </div>
              <div style={{ flex: 1, padding: '14px 8px', background: 'white', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', border: `1px solid ${BRAND.blue}30`, textAlign: 'center' }}>
                <Monitor size={22} color={BRAND.blue} style={{ margin: '0 auto 6px' }} />
                <p style={{ fontSize: '0.65rem', color: '#7a8599', fontWeight: 600 }}>PC</p>
              </div>
              <div style={{ flex: 1, padding: '14px 8px', background: 'white', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', border: '1px solid #e8edf2', textAlign: 'center' }}>
                <Globe size={22} color={BRAND.green} style={{ margin: '0 auto 6px' }} />
                <p style={{ fontSize: '0.65rem', color: '#7a8599', fontWeight: 600 }}>Online</p>
              </div>
            </div>
          )}
        </motion.div>
      </section>

      {/* Features */}
      <section id="funciones" style={{ padding: isMobile ? '40px 16px' : '80px 24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '30px' : '60px' }}>
          <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.2rem', fontWeight: 800, color: BRAND.dark, marginBottom: '10px' }}>Todo lo que necesitas</h2>
          <p style={{ fontSize: isMobile ? '0.85rem' : '1.05rem', color: '#7a8599', maxWidth: '480px', margin: '0 auto' }}>Herramientas diseñadas para hacer crecer tu negocio</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))', gap: isMobile ? '12px' : '20px' }}>
          {(c.features || []).map((f, i) => {
            const IconComp = ICON_MAP[f.title] || Package;
            return (
              <motion.div key={`feat-${f.title}-${i}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                style={{ background: 'white', borderRadius: '16px', padding: '28px', border: '1px solid #e8edf2', transition: 'box-shadow 0.3s, transform 0.3s' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 12px 40px ${BRAND.blue}10`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: `${BRAND.blue}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>
                  <IconComp size={22} color={BRAND.blue} />
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: BRAND.dark, marginBottom: '8px' }}>{f.title}</h3>
                <p style={{ color: '#7a8599', fontSize: '0.9rem', lineHeight: 1.6 }}>{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Plans */}
      <section id="planes" style={{ padding: isMobile ? '40px 16px' : '80px 24px', background: BRAND.lightBg }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '30px' : '60px' }}>
            <h2 style={{ fontSize: isMobile ? '1.5rem' : '2.2rem', fontWeight: 800, color: BRAND.dark, marginBottom: '10px' }}>Planes simples</h2>
            <p style={{ fontSize: isMobile ? '0.85rem' : '1.05rem', color: '#7a8599' }}>Empieza gratis, crece a tu ritmo</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: isMobile ? '16px' : '20px', maxWidth: '960px', margin: '0 auto' }}>
            {(c.plans || []).map((plan, i) => (
              <motion.div key={`plan-${plan.name}-${i}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.12 }}
                style={{
                  background: 'white', borderRadius: '20px', padding: '36px', textAlign: 'center',
                  border: plan.highlight ? `2px solid ${BRAND.blue}` : '1px solid #e8edf2',
                  transform: plan.highlight ? 'scale(1.04)' : 'none',
                  boxShadow: plan.highlight ? `0 20px 60px ${BRAND.blue}15` : 'none'
                }}>
                {plan.highlight && (
                  <span style={{ display: 'inline-block', padding: '4px 16px', borderRadius: '50px', background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.lightBlue})`, color: 'white', fontSize: '0.75rem', fontWeight: 700, marginBottom: '16px' }}>
                    Popular
                  </span>
                )}
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: BRAND.dark, marginBottom: '8px' }}>{plan.name}</h3>
                <p style={{ fontSize: '2rem', fontWeight: 900, color: BRAND.blue, marginBottom: '24px' }}>{plan.price}</p>
                <div style={{ textAlign: 'left', marginBottom: '24px' }}>
                  {(plan.features || []).map((feat, j) => (
                    <div key={`pf-${i}-${j}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: j < plan.features.length - 1 ? '1px solid #f3f5f8' : 'none' }}>
                      <CheckCircle size={16} color={BRAND.green} />
                      <span style={{ fontSize: '0.9rem', color: '#5a6577' }}>{feat}</span>
                    </div>
                  ))}
                </div>
                <a href="/registro"
                  style={{
                    display: 'block', padding: '14px', borderRadius: '12px', fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none',
                    background: plan.highlight ? `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.lightBlue})` : 'transparent',
                    color: plan.highlight ? 'white' : BRAND.blue,
                    border: plan.highlight ? 'none' : `2px solid ${BRAND.blue}`
                  }}>
                  Empezar ahora
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contacto" style={{ padding: isMobile ? '40px 16px' : '80px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? '1.4rem' : '2rem', fontWeight: 800, color: BRAND.dark, marginBottom: '12px' }}>{c.contact_title}</h2>
          <p style={{ fontSize: isMobile ? '0.85rem' : '1.05rem', color: '#7a8599', marginBottom: '24px', lineHeight: 1.7 }}>{c.contact_subtitle}</p>
          <a href={waLink} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: isMobile ? '14px 28px' : '18px 40px', borderRadius: '14px', background: '#25d366', color: 'white', fontWeight: 700, fontSize: isMobile ? '0.9rem' : '1.05rem', textDecoration: 'none', boxShadow: '0 8px 30px rgba(37,211,102,0.25)' }}
            data-testid="cta-whatsapp-bottom">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.638-1.218A11.937 11.937 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-2.168 0-4.178-.592-5.913-1.625l-.424-.252-2.753.724.736-2.688-.277-.44A9.774 9.774 0 012.182 12c0-5.418 4.4-9.818 9.818-9.818S21.818 6.582 21.818 12s-4.4 9.818-9.818 9.818z"/></svg>
            Escribir por WhatsApp
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: BRAND.dark, color: '#8a95a8', padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
          <Zap size={18} color={BRAND.green} />
          <span style={{ fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>{c.brand_name}<sup style={{ fontSize: '0.5em' }}>TM</sup></span>
        </div>
        <p style={{ fontSize: '0.8rem' }}>{new Date().getFullYear()} {c.brand_name}. Todos los derechos reservados.</p>
        <p style={{ fontSize: '0.75rem', marginTop: '6px' }}>{c.footer_tagline}</p>
      </footer>

      {/* Login Modal */}
      {showLogin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '20px' }}
          onClick={(e) => e.target === e.currentTarget && setShowLogin(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.25 }}
            style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '400px', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: BRAND.dark }}>Iniciar Sesión</h2>
                <p style={{ fontSize: '0.85rem', color: '#7a8599', marginTop: '2px' }}>Accede al panel de tu tienda</p>
              </div>
              <button onClick={() => setShowLogin(false)} style={{ background: '#f3f5f8', border: 'none', borderRadius: '10px', padding: '8px', cursor: 'pointer' }}>
                <X size={18} color="#7a8599" />
              </button>
            </div>

            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Usuario</label>
                <div style={{ position: 'relative' }}>
                  <User size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '13px' }} />
                  <input type="text" value={loginForm.username}
                    onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                    placeholder="tu usuario"
                    style={{ width: '100%', padding: '11px 11px 11px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                    data-testid="login-username" />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#5a6577', marginBottom: '6px' }}>Contraseña</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} color="#aab0bc" style={{ position: 'absolute', left: '12px', top: '13px' }} />
                  <input type={showPass ? 'text' : 'password'} value={loginForm.password}
                    onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="tu contraseña"
                    style={{ width: '100%', padding: '11px 40px 11px 36px', borderRadius: '10px', border: '1.5px solid #e0e5ec', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                    data-testid="login-password" />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: '12px', top: '11px', background: 'none', border: 'none', cursor: 'pointer', color: '#aab0bc' }}>
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loginLoading}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', background: `linear-gradient(135deg, ${BRAND.blue}, ${BRAND.lightBlue})`, color: 'white', fontWeight: 700, fontSize: '1rem', border: 'none', cursor: loginLoading ? 'wait' : 'pointer', opacity: loginLoading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                data-testid="login-submit">
                <LogIn size={18} />
                {loginLoading ? 'Entrando...' : 'Entrar a mi tienda'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f2f5' }}>
              <p style={{ fontSize: '0.85rem', color: '#7a8599' }}>
                ¿No tienes cuenta? <a href="/registro" style={{ color: BRAND.blue, fontWeight: 600, textDecoration: 'none' }}>Regístrate gratis</a>
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
